import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TransformIcon from '@mui/icons-material/Transform';
import TableChartIcon from '@mui/icons-material/TableChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CircleIcon from '@mui/icons-material/Circle';
import type { LineageChain, CanvasNodeKind } from '../canvas/types';

/* ── Color constants ─────────────────────────────────────── */
const SOURCE_COLOR = '#3b82f6';
const TARGET_COLOR = '#22c55e';
const EXPR_COLOR = '#ec4899';
const DIRECT_COLOR = '#64748b';
const JOIN_COLOR = '#8b5cf6';

/* ── Helpers ──────────────────────────────────────────────── */
const confidenceColor = (c: number): string =>
  c >= 0.9 ? '#22c55e' : c >= 0.6 ? '#f59e0b' : '#ef4444';

const operationLabel = (op: string): string => {
  const labels: Record<string, string> = {
    direct: 'Direct Mapping',
    expression: 'Expression',
    cast: 'Type Cast',
    set_constant: 'Set Constant',
    set_null: 'Set Null',
    aggregate: 'Aggregate',
    split: 'Split',
    pivot: 'Pivot',
    unpivot: 'Unpivot',
    join: 'Join',
    window: 'Window Function',
    constant: 'Constant',
    conditional: 'Conditional',
    filter: 'Filter',
    concat: 'Concatenation',
  };
  return labels[op.toLowerCase()] || op;
};

const opColor = (op: string): string => {
  const colors: Record<string, string> = {
    expression: EXPR_COLOR,
    direct: DIRECT_COLOR,
    join: JOIN_COLOR,
    aggregate: '#7c3aed',
    window: '#db2777',
    pivot: '#0891b2',
    unpivot: '#0891b2',
    conditional: '#d97706',
    filter: '#059669',
    set_constant: '#6366f1',
    concat: '#2563eb',
    cast: '#0d9488',
  };
  return colors[op.toLowerCase()] || '#f59e0b';
};

/* ── Vertical arrow between stages ────────────────────────── */
const StageArrowDown: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25 }}>
    <ArrowDownwardIcon sx={{ fontSize: 18, color: '#cbd5e1' }} />
  </Box>
);

/* ── Stage card wrapper (vertical) ────────────────────────── */
const StageCard: React.FC<{
  color: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ color, icon, title, subtitle, children }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: `1.5px solid ${color}40`,
      borderRadius: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        bgcolor: `${color}10`,
        px: 1.25,
        py: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderBottom: `1px solid ${color}20`,
      }}
    >
      {icon}
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ fontSize: 10, color, textTransform: 'uppercase', flex: 1 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ fontSize: 9, color: '#94a3b8' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box sx={{ px: 1.25, py: 0.75 }}>{children}</Box>
  </Box>
);

/* ── Single Chain Vertical View ───────────────────────────── */
const ChainVerticalFlow: React.FC<{
  chain: LineageChain;
  currentNodeKind?: CanvasNodeKind;
}> = ({ chain, currentNodeKind }) => {
  const hasSources = chain.sources.length > 0;
  const hasSteps = chain.steps.length > 0;
  const confPct = Math.round(chain.overallConfidence * 100);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* ── Source Fields Stage ── */}
      {hasSources && (
        <>
          <StageCard
            color={SOURCE_COLOR}
            icon={<StorageIcon sx={{ fontSize: 13, color: SOURCE_COLOR }} />}
            title={`Source${chain.sources.length > 1 ? 's' : ''}`}
          >
            {chain.sources.map((src, i) => (
              <Stack
                key={src.qualifiedName + i}
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mb: i < chain.sources.length - 1 ? 0.5 : 0 }}
              >
                <CircleIcon sx={{ fontSize: 6, color: SOURCE_COLOR }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 11,
                    color: currentNodeKind === 'sourceField' ? SOURCE_COLOR : '#475569',
                    fontWeight: currentNodeKind === 'sourceField' ? 700 : 400,
                  }}
                >
                  {src.table && src.field ? `${src.table}.${src.field}` : src.qualifiedName}
                </Typography>
              </Stack>
            ))}
          </StageCard>
          <StageArrowDown />
        </>
      )}

      {/* ── No source (constant/set_null) ── */}
      {!hasSources && (
        <>
          <StageCard
            color="#f59e0b"
            icon={<TransformIcon sx={{ fontSize: 13, color: '#f59e0b' }} />}
            title="Generated Value"
          >
            <Typography variant="caption" sx={{ fontSize: 11, color: '#475569' }}>
              {chain.transformationType
                ? operationLabel(chain.transformationType)
                : 'No source input'}
            </Typography>
          </StageCard>
          <StageArrowDown />
        </>
      )}

      {/* ── Transformation Steps ── */}
      {hasSteps &&
        chain.steps.map((step, i) => {
          const color = opColor(step.operation);
          const stepConfPct = Math.round(step.confidence * 100);

          return (
            <React.Fragment key={step.operation + i}>
              <StageCard
                color={color}
                icon={<TransformIcon sx={{ fontSize: 13, color }} />}
                title={operationLabel(step.operation)}
                subtitle={`${stepConfPct}%`}
              >
                {step.expression && (
                  <Tooltip title={step.expression} arrow>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10,
                        color: '#64748b',
                        fontFamily: '"Fira Code", "JetBrains Mono", Consolas, monospace',
                        display: 'block',
                        lineHeight: 1.5,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {step.expression}
                    </Typography>
                  </Tooltip>
                )}
                {step.description && (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 10, color: '#94a3b8', display: 'block', mt: 0.25 }}
                  >
                    {step.description}
                  </Typography>
                )}
                {step.aiGenerated && (
                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ mt: 0.25 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 10, color: 'primary.main' }} />
                    <Typography variant="caption" sx={{ fontSize: 9, color: 'primary.main' }}>
                      AI-generated
                    </Typography>
                  </Stack>
                )}
                {step.parameters && Object.keys(step.parameters).filter(k => k !== 'expression').length > 0 && (
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                    {Object.entries(step.parameters)
                      .filter(([key]) => key !== 'expression')
                      .map(([key, value]) => (
                        <Chip
                          key={key}
                          size="small"
                          label={`${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`}
                          sx={{
                            height: 18,
                            fontSize: 9,
                            fontWeight: 500,
                            backgroundColor: `${color}15`,
                            color,
                            fontFamily: '"Fira Code", "JetBrains Mono", Consolas, monospace',
                          }}
                        />
                      ))
                    }
                  </Box>
                )}
              </StageCard>
              <StageArrowDown />
            </React.Fragment>
          );
        })}

      {/* ── Direct mapping (no steps) ── */}
      {!hasSteps && (
        <>
          <StageCard
            color={DIRECT_COLOR}
            icon={<ArrowForwardIcon sx={{ fontSize: 13, color: DIRECT_COLOR }} />}
            title="Direct Mapping"
          >
            <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>
              Value passed through without transformation
            </Typography>
          </StageCard>
          <StageArrowDown />
        </>
      )}

      {/* ── Target Field Stage ── */}
      <StageCard
        color={TARGET_COLOR}
        icon={<TableChartIcon sx={{ fontSize: 13, color: TARGET_COLOR }} />}
        title="Target"
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <CircleIcon sx={{ fontSize: 6, color: TARGET_COLOR }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              color: currentNodeKind === 'targetField' ? TARGET_COLOR : '#1e293b',
              fontWeight: currentNodeKind === 'targetField' ? 700 : 600,
            }}
          >
            {chain.target.table && chain.target.field
              ? `${chain.target.table}.${chain.target.field}`
              : chain.target.qualifiedName}
          </Typography>
        </Stack>
      </StageCard>

      {/* ── Confidence & Explanation footer ── */}
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ mt: 0.75, px: 0.5, flexWrap: 'wrap', gap: 0.5 }}
      >
        <Chip
          size="small"
          label={`${confPct}% confidence`}
          sx={{
            height: 20,
            fontSize: 10,
            fontWeight: 600,
            bgcolor: `${confidenceColor(chain.overallConfidence)}18`,
            color: confidenceColor(chain.overallConfidence),
          }}
        />
        {chain.steps.some((s) => s.aiGenerated) && (
          <Chip
            size="small"
            icon={<AutoAwesomeIcon sx={{ fontSize: 12 }} />}
            label="AI"
            variant="outlined"
            color="primary"
            sx={{ height: 20, fontSize: 10 }}
          />
        )}
        {chain.explanation && (
          <Tooltip title={chain.explanation} arrow>
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                color: '#94a3b8',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 180,
                cursor: 'help',
              }}
            >
              {chain.explanation}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

/* ── Main Component ───────────────────────────────────────── */
interface FieldLineageChainProps {
  currentNode: string;
  currentNodeKind?: CanvasNodeKind;
  chains: LineageChain[];
  /** Legacy fallback */
  upstream?: string[];
  downstream?: string[];
}

const FieldLineageChain: React.FC<FieldLineageChainProps> = ({
  currentNode,
  currentNodeKind,
  chains,
  upstream = [],
  downstream = [],
}) => {
  // Rich chain view
  if (chains.length > 0) {
    return (
      <Box sx={{ p: 1.5, overflowY: 'auto' }}>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#64748b',
            display: 'block',
            mb: 1,
            fontSize: 10,
          }}
        >
          Field Lineage · {chains.length} mapping{chains.length !== 1 ? 's' : ''}
        </Typography>
        <Stack spacing={2}>
          {chains.map((chain) => (
            <ChainVerticalFlow
              key={chain.mappingId}
              chain={chain}
              currentNodeKind={currentNodeKind}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  // Legacy fallback: simple vertical flow from upstream → current → downstream
  if (upstream.length > 0 || downstream.length > 0) {
    return (
      <Box sx={{ p: 1.5, overflowY: 'auto' }}>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#64748b',
            display: 'block',
            mb: 1,
            fontSize: 10,
          }}
        >
          Field Lineage
        </Typography>

        <Stack spacing={0}>
          {/* Upstream sources */}
          {upstream.length > 0 && (
            <>
              <StageCard
                color={SOURCE_COLOR}
                icon={<StorageIcon sx={{ fontSize: 13, color: SOURCE_COLOR }} />}
                title={`${upstream.length} Source${upstream.length !== 1 ? 's' : ''}`}
              >
                {upstream.map((src) => (
                  <Stack key={src} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                    <CircleIcon sx={{ fontSize: 6, color: SOURCE_COLOR }} />
                    <Typography variant="caption" sx={{ fontSize: 11, color: '#475569' }}>
                      {src}
                    </Typography>
                  </Stack>
                ))}
              </StageCard>
              <StageArrowDown />
            </>
          )}

          {/* Current node */}
          <StageCard
            color={currentNodeKind === 'sourceField' ? SOURCE_COLOR : TARGET_COLOR}
            icon={
              currentNodeKind === 'sourceField' ? (
                <StorageIcon sx={{ fontSize: 13, color: SOURCE_COLOR }} />
              ) : (
                <TableChartIcon sx={{ fontSize: 13, color: TARGET_COLOR }} />
              )
            }
            title="Selected"
          >
            <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, color: '#1e293b' }}>
              {currentNode}
            </Typography>
          </StageCard>

          {/* Downstream targets */}
          {downstream.length > 0 && (
            <>
              <StageArrowDown />
              <StageCard
                color={TARGET_COLOR}
                icon={<TableChartIcon sx={{ fontSize: 13, color: TARGET_COLOR }} />}
                title={`${downstream.length} Target${downstream.length !== 1 ? 's' : ''}`}
              >
                {downstream.map((tgt) => (
                  <Stack key={tgt} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                    <CircleIcon sx={{ fontSize: 6, color: TARGET_COLOR }} />
                    <Typography variant="caption" sx={{ fontSize: 11, color: '#475569' }}>
                      {tgt}
                    </Typography>
                  </Stack>
                ))}
              </StageCard>
            </>
          )}
        </Stack>
      </Box>
    );
  }

  // Empty state
  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="body2" color="text.disabled">
        No lineage available — this field has no mappings.
      </Typography>
    </Box>
  );
};

export default FieldLineageChain;
